import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';

/** Only bundle-load failures qualify. API, permission and application errors
 * must not be disguised as a deployment or automatically retried. */
export function isBundleLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|ChunkLoadError/i.test(message);
}

@Component({
  selector: 'app-route-recovery', standalone: true,
  template: `
    @if (failedUrl()) {
      <section class="recovery" aria-label="Page loading recovery">
        <div role="alert"><h2>{{ heading() }}</h2><p>{{ message() }}</p></div>
        <p>Any current page stays open. Save or copy any unsaved work before retrying or reloading. Nothing is submitted automatically.</p>
        @if (confirming()) {
          <p id="reload-warning"><strong>Reloading will discard unsaved changes on this page.</strong> Continue only after saving or copying your work.</p>
          <div class="actions">
            <button id="confirm-page-reload" type="button" (click)="reload()" aria-describedby="reload-warning">Discard unsaved changes and reload</button>
            <button type="button" (click)="setConfirmation(false)">Keep working</button>
          </div>
        } @else {
          <div class="actions">
            <button type="button" (click)="retry()" [disabled]="busy()">{{ busy() ? 'Retrying…' : 'Retry page' }}</button>
            <button id="request-page-reload" type="button" (click)="setConfirmation(true)">Reload latest version…</button>
            <button type="button" (click)="dismiss()">Dismiss</button>
          </div>
        }
      </section>
    }
  `,
  styles: [`
    .recovery { position:fixed; z-index:10000; bottom:16px; right:16px; width:min(540px,calc(100vw - 32px)); max-height:70vh; overflow:auto; padding:20px; box-sizing:border-box; background:#fff; color:#243547; border:2px solid #7c642a; box-shadow:0 4px 24px #0004; font:14px/1.5 var(--sans,sans-serif); overflow-wrap:anywhere; }
    h2 { font-size:18px; margin:0 0 8px; } p { margin:0 0 12px; }
    .actions { display:flex; flex-wrap:wrap; gap:8px; }
    button { font:inherit; padding:10px 12px; background:transparent; color:inherit; border:1px solid currentColor; cursor:pointer; white-space:normal; }
    button:focus-visible { outline:3px solid #bd6500; outline-offset:2px; }
    button:disabled { opacity:.6; cursor:wait; }
    :host-context(.chrome-light) .recovery { background:#fff; color:#243547; }
    :host-context(body:has(.admin-shell:not(.chrome-light))) .recovery { background:#1f2b38; color:#edf3f8; border-color:#d7b85c; }
  `],
})
export class RouteRecoveryComponent {
  private router = inject(Router);
  readonly failedUrl = signal('');
  readonly confirming = signal(false);
  readonly busy = signal(false);
  readonly heading = signal('This page could not load');
  readonly message = signal('Check your connection and retry. A deployment may also have replaced this page’s files.');
  private check = 0;

  setConfirmation(value: boolean): void {
    this.confirming.set(value);
    setTimeout(() => document.getElementById(value ? 'confirm-page-reload' : 'request-page-reload')?.focus());
  }

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationEnd) this.dismiss();
      if (event instanceof NavigationError && isBundleLoadError(event.error)) {
        this.failedUrl.set(event.url);
        this.confirming.set(false);
        void this.classify(++this.check);
      }
    });
  }

  private async classify(check: number): Promise<void> {
    this.heading.set('This page could not load');
    this.message.set('Check your connection and retry. A deployment may also have replaced this page’s files.');
    if (!navigator.onLine) {
      this.heading.set('You appear to be offline');
      this.message.set('Reconnect to the internet first. Then retry or reload this page. Reloading while offline will not fix this.');
      return;
    }
    try {
      const response = await fetch('/index.html', { cache: 'no-store', signal: AbortSignal.timeout(5000) });
      if (!response.ok) return;
      const latest = new DOMParser().parseFromString(await response.text(), 'text/html');
      const main = (doc: Document) => doc.querySelector<HTMLScriptElement>('script[type="module"][src*="main"]')?.getAttribute('src');
      if (check !== this.check || !this.failedUrl()) return;
      if (main(document) && main(latest) && main(document) !== main(latest)) {
        this.heading.set('A newer admin version is available');
        this.message.set('This tab uses older application files. Save or copy your work, then reload the latest version.');
      }
    } catch { /* A network error is not evidence of a new deployment. */ }
  }

  dismiss(): void { this.check++; this.failedUrl.set(''); this.confirming.set(false); }

  async retry(): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    let loaded = false;
    try { loaded = await this.router.navigateByUrl(this.failedUrl()); }
    catch { /* NavigationError keeps the recovery notice visible. */ }
    finally { this.busy.set(false); }
    if (loaded || !this.failedUrl()) return;
    // Some browsers (Chrome) remember a failed page file for the life of the tab,
    // so a second in-tab attempt cannot succeed. Point to the explicit reload
    // instead of offering a Retry that will keep failing.
    this.check++;
    if (!navigator.onLine) {
      this.heading.set('You appear to be offline');
      this.message.set('Reconnect to the internet first. Then reload this page. Reloading while offline will not fix this.');
      return;
    }
    this.heading.set('Reload to open this page');
    this.message.set('This tab cannot fetch the page files again. A reload gets a fresh copy.');
    this.setConfirmation(true);
  }

  reload(): void {
    // Explicit confirmation only; no automatic reloads or reload loops.
    const target = new URL(this.failedUrl(), location.origin);
    if (target.origin !== location.origin) return;
    location.assign(target.href);
  }
}
